import { Add, CheckBoxOutlined } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Chip,
  ClickAwayListener,
  Grid,
  IconButton,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Popper,
  Skeleton,
  TableBody,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import SvgIcon from '@mui/material/SvgIcon';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/system';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import { format } from 'date-fns';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import loader from 'src/components/LoaderImg/loader.gif';
import { Scrollbar } from 'src/components/scrollbar';
import { DocDuePnAlert, complianceOnBehalf, multiPayerFlag } from 'src/config';
import { deleteDocumentAlertTreatment, recheckDischarge } from 'src/config';
import {
  COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE,
  isComplianceCreateMode,
} from './compliance-document-on-behalf';
import SelectTargetTherapist from './select-target-therapist';
import useFeatureToggle, { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import ClockInOutModal from 'src/pages/therapist-dashboard/time-in-out/clock-in-out-modal';
import { paths } from 'src/paths';
import { useScopeChecker } from 'src/rbac';
import { canCreateFormType, canUpdateFormType } from 'src/rbac/document-form-access';
import {
  checkPendingDocsAlertAction,
  deleteRequiredDocumentAction,
  getEocDocumentCreateDataAction,
  recheckDischargeOverrideStatusAction,
  requestDocDueSettings,
  resetApproveRecheckDischarge,
  resetDeleteRequiredDocumentAction,
  resetEocDocumentCreateDataAction,
  resetPendingDocsAlertAction,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import { getEpisodeOfCareRequiredDocument } from 'src/redux/actions/patientEpisodeOfCare';
import { getRealmName } from 'src/routes/AuthContext';
import { SignatureAction } from 'src/types/signatureContext';
import { checkSupervisorPermission } from 'src/utils/checkSignatureAccess';
import GetDocumentIcon from 'src/utils/get-document-icons';
import { getDisplayString } from 'src/utils/getDaysBtDate';
import useDialogVisibility from '../../utils/dialog-hide';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import DischargeRecheckModal from '../common-modals/discharge-recheck-modal';
import InfoConfirmModal from '../common-modals/info-confirm-modal';
import Status from '../status/status';
import SucessModal from '../sucess-modal';
import { getRecheckDischargeDecision } from './recheck-discharge-gate';
import CommonConfirmDeleteModal from './delete-alert-modal';
import { DraftDocument, getDraftDocuments } from 'src/services/documentService/getDraftDocuments';
import { getOldestRequiredDocumentUuid } from 'src/utils/getOldestRequiredDocumentUuid';
import WarningModal from '../therapyPlanning_2.0/commonComponent/warning-modal';
import { isClinicalDiscipline, toClinicalDiscipline } from './clinical-discipline';
import { shouldShowPayerWarning } from 'src/theme/utils';
import { getPlanOfCare } from 'src/redux/actions/therapyPlanning';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { eocRowTestId, eocTestId } from 'src/components/common-modals/test-ids';

type Props = {
  activeTab: string;
  abortControllerScheduledDocumentRef: any;
  openPrintMode?: any;
  isPatientInfoMissing?: boolean;
};

interface DocumentData {
  documentType: string;
  dueDate: string;
  lastUpdatedOn: string;
  status: string;
  uuid: string;
  formId: string;
  dueDateString: string;
  lastUpdatedOnString: string;
  type: string;
  formType: string;
  visits: string;
  // THIN-8393: the day rule this alert was built under (its reporting period's span); null when the
  // backend has no reporting period for the row, in which case the patient's current setting is used
  maxProgressNoteDays?: number | null;
}

interface NavigateDoc {
  formId: string;
  formType: string;
  fromMaster: boolean;
  uuid: string;
}

const columns: any = [
  { id: 'documentType', label: 'Document Type' },
  { id: 'dueDate', label: 'Due Date' },
  { id: 'visit', label: 'Treatment Visits Completed' },
  { id: 'dueStatus', label: 'Status' },
  { id: 'action', label: '', align: 'right' },
];

const checkIsRecertOverdue = (requiredDocs: any[]) => {
  return requiredDocs.some(
    (doc) => doc?.formType?.includes('WEEKLY_PROGRESS_NOTE') && doc?.status === 'OVERDUE'
  );
};

const ViewDocumentList: FC<Props> = (props: Props) => {
  const { activeTab, abortControllerScheduledDocumentRef, openPrintMode, isPatientInfoMissing } =
    props;
  const { formattedTime, isRunning, startStopwatch, stopStopwatch, time } = useStopwatch();

  const { canShowDialog: canShowClockInDialog } = useDialogVisibility('clock_in_do_not_show');
  const location = useLocation();
  const navigate = useNavigate();
  const { patients, patientDetails, episodeOfCare, details, createDocument, editDocument } = paths;
  const anchorRef = useRef<any>(null);
  const episodeOfCareId = location?.state?.episodeOfCareId;
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const getAllTemplates = useSelector((state: any) => state.getAllTemplatesReducer);
  const [allTemplates, setAllTemplates] = useState<any>([]);
  const [draftDocuments, setDraftDocuments] = useState<string[]>([]);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const isPatientDataLoaded = useSelector((state: any) => state.getSinglePatientReducer.loading);
  const therapistDiscipline = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.discipline
  );

  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );
  const [isMultiPayer]: any = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');

  const { refetch: refetchClockInOutVisibility } = useClockInOutButtonVisibility();

  const therapistId = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data?.providerId
  );
  const episodeOfCareRequiredDocumentData = useSelector(
    (state: any) => state.getEpisodeOfCareRequiredDocumentReducer
  );
  const requiredDocs = useSelector(
    (state: any) => state.getEpisodeOfCareRequiredDocumentReducer?.response?.content
  );
  const docSettings = useSelector((state: any) => state.dueDocSettingsReducer?.response?.data);
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);
  // Segment B: eval/recert create + update gates migrated to the instrumented scopeKey path.
  const hasScope = useScopeChecker();
  const getSignatureAccess = useSelector((state: any) => state.getSignatureAccessReducer);
  //  BRK-2074: create on behalf (epic BRK-2055). The fork exists ONLY where the legacy
  //  discipline gate says no — a discipline-matched holder keeps the legacy self-create flow
  //  untouched. The target's need-supervision maps live in their own reducer; the caller-scoped
  //  map above (fetched at profile load, read by every legacy surface) is never clobbered.
  const targetSignatureAccess = useSelector((state: any) => state.getTargetSignatureAccessReducer);
  const [isComplianceOnBehalf] = useFeatureToggleWhenReady(complianceOnBehalf, 'ENTERPRISE');
  const complianceCreateMode = isComplianceCreateMode({
    complianceSplitOn: isComplianceOnBehalf,
    hasDocumentScope: hasScope(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
  });
  //  The pending dialog intent ({intent:'MENU'} | {intent:'ROW', document}) and, once confirmed
  //  on the menu path, the picked target + reason the next template click threads onto the
  //  create hand-off.
  const [onBehalfCreate, setOnBehalfCreate] = useState<any>(null);
  const [onBehalfTarget, setOnBehalfTarget] = useState<any>(null);
  const complianceCreateForTab = complianceCreateMode && therapistDiscipline !== activeTab;
  //  BRK-2165: Create Document and ReCheck for Discharge are both pinned to the header's right
  //  edge; ReCheck shifts left only when Create Document is showing, so they share one condition.
  const showCreateDocument =
    (therapistDiscipline === activeTab || complianceCreateForTab) && !openPrintMode;
  const complianceCreateForRow = (row: any) =>
    complianceCreateMode && therapistDiscipline !== row?.type;

  //  A tab switch orphans any in-flight on-behalf context (the target list, maps, and menu all
  //  belong to the previous discipline) — drop it and close the menu it opened. Legacy state is
  //  untouched: this fires only when compliance state was actually set.
  useEffect(() => {
    if (onBehalfCreate || onBehalfTarget) {
      setOnBehalfCreate(null);
      setOnBehalfTarget(null);
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const checkDeleteRequiredDocument = useSelector(
    (state: any) => state.deleteRequiredDocumentReducer
  );
  const checkPendingDocsAlert = useSelector((state: any) => state.checkPendingDocsAlert);
  const recheckOverrideStatus = useSelector(
    (state: any) => state?.recheckDischargeOverrideStatusReducer?.response
  );
  // Mirror the Create Document → Discharge availability. The discharge template is
  // dropped from allTemplates in exactly the two cases where there's nothing to
  // discharge: a discharge document already exists for the discipline, or the
  // discipline has no open certification. Hide the "ReCheck for Discharge" button
  // whenever Discharge isn't an available Create Document option.
  const hasDischargeTemplate = allTemplates?.some((template: any) =>
    (template?.formType ?? '').toUpperCase().includes('DISCHARGE')
  );
  const [isDeleteAlertOn] = useFeatureToggle(deleteDocumentAlertTreatment, 'FACILITY');

  const [isRecheckDischarge] = useFeatureToggleWhenReady(recheckDischarge, 'ENTERPRISE');
  const [isDocDuePNAlert] = useFeatureToggleWhenReady(DocDuePnAlert, 'FACILITY');

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  // Fetch whether the "ReCheck for Discharge" action is available for this facility/user.
  useEffect(() => {
    dispatch(recheckDischargeOverrideStatusAction());
  }, [dispatch]);

  const getFilteredTemplates = (filterDocList: { formType?: string }[]) => {
    const tabToKey: Record<string, keyof typeof planOfCareDetails> = {
      PT: 'checkPtActive',
      OT: 'checkOtActive',
      SLP: 'checkSlpActive',
    };

    const isActive = planOfCareDetails?.[tabToKey[activeTab]] ?? false;

    return filterDocList?.filter((template) => {
      const formType = (template?.formType ?? '').trim().toUpperCase();

      // Legacy ADDENDUM_NOTE was a Jotform form, which is retired; only the Reform addendum
      // (ADDENDUM_NOTE_REFORM) can be created now.
      if (formType === 'ADDENDUM_NOTE') {
        return false;
      }

      if (isActive) {
        return !formType.includes('EVALUATION');
      } else {
        return formType.includes('EVALUATION') || formType === 'ADDENDUM_NOTE_REFORM';
      }
    });
  };

  useEffect(() => {
    if (!getAllTemplates.areGetTemplates && getAllTemplates.response !== null) return;

    // THIN-8023: no fallback to SLP — a non-clinical tab value means there is nothing to fetch yet.
    const discipline = toClinicalDiscipline(activeTab);
    if (!discipline) return;
    const filteredTemplate = getAllTemplates.response?.filter((item: any) => {
      if (!canCreateFormType(hasScope, item.formType)) {
        // Recert templates need Create-Recertification, eval templates need Create-Eval.
        return;
      } else if (
        item.formType === 'TRANSITIONAL_EVALUATION_PT' ||
        item.formType === 'TRANSITIONAL_EVALUATION_OT' ||
        item.formType === 'TRANSITIONAL_EVALUATION_SLP'
      ) {
        //  Legacy transitional evaluations are superseded by their _REFORM_ flavour.
        return;
      } else if (
        (item.formType === 'TRANSITIONAL_EVALUATION_REFORM_PT' ||
          item.formType === 'TRANSITIONAL_EVALUATION_REFORM_OT' ||
          item.formType === 'TRANSITIONAL_EVALUATION_REFORM_SLP') &&
        item.formType !== `TRANSITIONAL_EVALUATION_REFORM_${discipline}`
      ) {
        //  Only the active discipline's transitional evaluation is offered.
        return;
      } else {
        return item;
      }
    });

    const reformTemplates: typeof filteredTemplate = [];

    if (filteredTemplate) {
      filteredTemplate.forEach((template: any) => {
        if (
          template.formType === 'EVALUATION_REFORM_PT' ||
          template.formType === 'EVALUATION_REFORM_OT' ||
          template.formType === 'EVALUATION_REFORM_SLP' ||
          template.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_PT' ||
          template.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_OT' ||
          template.formType === 'WEEKLY_PROGRESS_NOTE_REFORM_SLP' ||
          template.formType === 'RECERTIFICATION_REFORM_OT' ||
          template.formType === 'RECERTIFICATION_REFORM_PT' ||
          template.formType === 'RECERTIFICATION_REFORM_SLP' ||
          template.formType === 'DISCHARGE_REFORM_PT' ||
          template.formType === 'DISCHARGE_REFORM_OT' ||
          template.formType === 'DISCHARGE_REFORM_SLP'
        ) {
          const originalFormType = template.formType.replace('_REFORM_', '_');
          reformTemplates.push(originalFormType);
        }
      });
      const newUpdatedList = filteredTemplate.filter((template: any) => {
        if (template.formType.includes('TRANSITIONAL_EVALUATION')) {
          return template.formType.includes('_REFORM_');
        }
        return !reformTemplates.includes(template.formType);
      });
      setAllTemplates(getFilteredTemplates(newUpdatedList));
    }
  }, [getAllTemplates, planOfCareDetails]);

  useEffect(() => {
    // THIN-8023: no fallback to SLP — a non-clinical tab value means there is nothing to fetch yet.
    const discipline = toClinicalDiscipline(activeTab);
    if (!discipline) return;
    const realm = getRealmName();

    if (!realm) {
      return;
    }

    getDraftDocuments(episodeOfCareId, discipline, getRealmName() || '').then(
      (response: DraftDocument[]) => {
        const draftDocuments = response.map((draftDocument) => draftDocument.documentType);
        setDraftDocuments(draftDocuments);
      }
    );
  }, [activeTab, episodeOfCareId]);

  // Fetch plan of care details if not available
  useEffect(() => {
    if (!planOfCareDetails && patientData?.uuid && episodeOfCareId) {
      dispatch(
        getPlanOfCare({
          patientId: patientData.uuid,
          episodeOfCareUuid: episodeOfCareId,
        })
      );
    }
  }, [planOfCareDetails, patientData?.uuid, episodeOfCareId, dispatch]);

  const callRequiredDocuments = () => {
    dispatch(
      getEpisodeOfCareRequiredDocument({
        therapistId: therapistId,
        signal: abortControllerScheduledDocumentRef.current.signal,
        params: {
          episodeOfCareId: episodeOfCareId,
          type: activeTab,
          page,
          pageSize: 10,
          sortBy: 'dueDate',
          sortDirection: 'asc',
        },
      })
    );
    // THIN-8023: docs-due settings exist per clinical discipline only; the server rejects anything else.
    if (isClinicalDiscipline(activeTab)) {
      dispatch(
        requestDocDueSettings({
          params: {
            payerType: episodeOfCareData?.payerPlanEntity?.payerPlanType,
            discipline: activeTab,
          },
        })
      );
    }
  };

  const [successModal, setSuccessModal] = useState({
    success: false,
    warning: false,
  });

  const handleCloseSuccess = () => {
    setSuccessModal({
      success: false,
      warning: false,
    });
  };

  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);

  //clock in modal

  const [isClockIn, setIsClockIn] = useState(false);
  const [notToShowAgain, setNotToShowAgain] = useState(false);
  const [actionToBeContinue, setActionToBeContinue] = useState('');
  const [navigateDoc, setNavigateDoc] = useState<NavigateDoc | null>(null);
  const [editDocumentAction, setEditDocumentAction] = useState<any>('');

  //pagination logic

  const [rowPerPage, setRowPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrderArray, setSortArray] = useState<any>(['desc', 'desc', 'desc', 'desc', 'desc']);

  const onPageChange = (event: any, newPage: number) => {
    dispatch(
      getEpisodeOfCareRequiredDocument({
        therapistId: therapistId,
        signal: abortControllerScheduledDocumentRef.current.signal,
        params: {
          episodeOfCareId: episodeOfCareId,
          type: activeTab,
          page: newPage,
          pageSize: rowPerPage,
          sortBy: sortBy,
          sortDirection: getSortOrder(sortBy),
        },
      })
    );
    setPage(newPage);
  };
  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);
    dispatch(
      getEpisodeOfCareRequiredDocument({
        therapistId: therapistId,
        signal: abortControllerScheduledDocumentRef.current.signal,
        params: {
          episodeOfCareId: episodeOfCareId,
          type: activeTab,
          page: 0,
          pageSize: newRowsPerPage,
          sortBy: sortBy,
          sortDirection: getSortOrder(sortBy),
        },
      })
    );
    setPage(0);
  };

  const resetPagination = () => {
    setPage(0);
    setRowPerPage(10);
    setSortBy('dueDate');
    setSortArray(['desc', 'desc', 'desc', 'desc', 'desc']);
  };

  useEffect(() => {
    if (!activeTab || !episodeOfCareData) return;
    abortControllerScheduledDocumentRef.current = new AbortController();
    callRequiredDocuments();
    resetPagination();
  }, [activeTab, episodeOfCareData]);

  useEffect(() => {
    if (!checkoutResponse?.isGetCheckIn && checkoutResponse?.response === null) return;
    if (!checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      setSuccessModal({
        success: false,
        warning: true,
      });
      return;
    }
    if (checkoutResponse.isGetCheckIn && checkoutResponse?.response !== null) {
      if (checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS') {
        setSuccessModal({
          success: true,
          warning: false,
        });
        if (actionToBeContinue === 'CREATE_NEW_DOCUMENT') {
          dispatch(resetEocDocumentCreateDataAction());
          dispatch(
            getEocDocumentCreateDataAction({
              document: navigateDoc,
              activeTab,
              patientData,
              episodeOfCareId,
            })
          );
          if (
            planOfCareDetails &&
            shouldShowPayerWarning(navigateDoc, planOfCareDetails?.isPayerPresent, isMultiPayer)
          ) {
            setIsClockIn(false);
            setIsWarningModalOpen(true);
            return;
          }

          if (
            navigateDoc?.formType?.includes('RECERTIFICATION') &&
            checkIsRecertOverdue(requiredDocs)
          ) {
            setIsClockIn(false);
            setShowRecertModal(true);
            return;
          }
          // discharge doc prevent
          if (isRecheckDischarge && navigateDoc?.formType?.includes('DISCHARGE')) {
            dispatch(
              checkPendingDocsAlertAction({
                eocId: episodeOfCareId,
                params: {
                  discipline: activeTab,
                  localDate: moment(new Date()).format('YYYY-MM-DD'),
                },
              })
            );
            return;
          }

          navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
            state: {
              document: navigateDoc,
              activeTab,
              patientData,
              episodeOfCareId,
            },
          });
          return;
        }
        if (actionToBeContinue === 'EDIT_DOCUMENT') {
          dispatch(resetEocDocumentCreateDataAction());
          dispatch(
            getEocDocumentCreateDataAction({
              document: editDocumentAction,
              activeTab,
              patientData,
              episodeOfCareId,
              requiredDocument: true,
            })
          );

          if (
            planOfCareDetails &&
            shouldShowPayerWarning(
              editDocumentAction,
              planOfCareDetails?.isPayerPresent,
              isMultiPayer
            )
          ) {
            setIsWarningModalOpen(true);
            return;
          }

          if (
            editDocumentAction?.formType?.includes('RECERTIFICATION') &&
            checkIsRecertOverdue(requiredDocs)
          ) {
            setShowRecertModal(true);
            return;
          }
          // discharge doc prevent
          if (isRecheckDischarge && editDocumentAction?.formType?.includes('DISCHARGE')) {
            dispatch(
              checkPendingDocsAlertAction({
                eocId: episodeOfCareId,
                params: {
                  discipline: activeTab,
                  localDate: moment(new Date()).format('YYYY-MM-DD'),
                },
              })
            );
            return;
          }
          navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
            state: {
              document: editDocumentAction,
              activeTab,
              patientData,
              episodeOfCareId,
              requiredDocument: true,
            },
          });
        }
      }
    }
  }, [checkoutResponse]);

  const handleIsClock = () => {
    if (actionToBeContinue === 'CREATE_NEW_DOCUMENT') {
      dispatch(resetEocDocumentCreateDataAction());
      dispatch(
        getEocDocumentCreateDataAction({
          document: navigateDoc,
          activeTab,
          patientData,
          episodeOfCareId,
        })
      );

      if (
        planOfCareDetails &&
        shouldShowPayerWarning(navigateDoc, planOfCareDetails?.isPayerPresent, isMultiPayer)
      ) {
        setIsClockIn(false);
        setIsWarningModalOpen(true);
        return;
      }

      if (
        navigateDoc?.formType?.includes('RECERTIFICATION') &&
        checkIsRecertOverdue(requiredDocs)
      ) {
        setIsClockIn(false);
        setShowRecertModal(true);
        return;
      }

      // discharge doc prevent
      if (isRecheckDischarge && navigateDoc?.formType?.includes('DISCHARGE')) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: episodeOfCareId,
            params: {
              discipline: activeTab,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        // setNotToShowAgain(true);
        setIsClockIn(false);
        return;
      }
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document: navigateDoc,
          activeTab,
          patientData,
          episodeOfCareId,
        },
      });
      return;
    }
    if (actionToBeContinue === 'EDIT_DOCUMENT') {
      dispatch(resetEocDocumentCreateDataAction());
      dispatch(
        getEocDocumentCreateDataAction({
          document: editDocumentAction,
          activeTab,
          patientData,
          episodeOfCareId,
          requiredDocument: true,
        })
      );

      if (
        planOfCareDetails &&
        shouldShowPayerWarning(editDocumentAction, planOfCareDetails?.isPayerPresent, isMultiPayer)
      ) {
        setIsWarningModalOpen(true);
        return;
      }

      if (
        editDocumentAction?.formType?.includes('RECERTIFICATION') &&
        checkIsRecertOverdue(requiredDocs)
      ) {
        setShowRecertModal(true);
        return;
      }
      // discharge doc prevent
      if (isRecheckDischarge && editDocumentAction?.formType?.includes('DISCHARGE')) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: episodeOfCareId,
            params: {
              discipline: activeTab,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        // setNotToShowAgain(true);
        setIsClockIn(false);
        return;
      }
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document: editDocumentAction,
          activeTab,
          patientData,
          episodeOfCareId,
          requiredDocument: true,
        },
      });
    }
    setNotToShowAgain(true);
    setIsClockIn(false);
  };

  const [showDischargeModal, setShowDischargeModal] = useState<boolean>(false);
  // Which flow triggered the pending-docs check. "Create Document → Discharge" skips the
  // modal and navigates straight to creating the doc; the "ReCheck for Discharge" button
  // opens the modal.
  const dischargeCheckSourceRef = useRef<'CREATE_DOC' | 'RECHECK'>('CREATE_DOC');
  // Whether the open discharge modal was launched from the "ReCheck for Discharge" button.
  // Drives the modal's isRecheckFlow: RECHECK → button just closes; CREATE_DOC → navigates.
  const [isRecheckDischargeModal, setIsRecheckDischargeModal] = useState(false);

  // Approve-recheck-discharge result → drive a SimpleSnackbar here (the modal closes on
  // success, so the message must come from this still-mounted parent). Detect the loading
  // true→false transition so a persisted/stale result doesn't re-trigger.
  const approveDsnState = useSelector((state: any) => state?.approveRecheckDischargeReducer);
  const wasApprovingDsnRef = useRef(false);
  const [approvePopUp, setApprovePopUp] = useState({ message: '', type: '' });
  useEffect(() => {
    if (approveDsnState?.loading) {
      wasApprovingDsnRef.current = true;
      return;
    }
    if (!wasApprovingDsnRef.current) return;
    wasApprovingDsnRef.current = false;

    if (approveDsnState?.isSuccess) {
      setApprovePopUp({
        message: approveDsnState?.response?.message || 'Recheck discharge approved successfully',
        type: 'SUCCESS',
      });
    } else if (approveDsnState?.response) {
      setApprovePopUp({
        message:
          approveDsnState?.response?.message ||
          approveDsnState?.response?.error ||
          'Failed to approve the discharge.',
        type: 'ERROR',
      });
    }
    dispatch(resetApproveRecheckDischarge());
    setTimeout(() => setApprovePopUp({ message: '', type: '' }), 3000);
  }, [approveDsnState?.loading, approveDsnState?.isSuccess]);

  const [showRecertModal, setShowRecertModal] = useState<boolean>(false);

  const handleContinueRecert = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
      state: {
        document: navigateDoc,
        activeTab,
        patientData,
        episodeOfCareId,
      },
    });
  };

  useEffect(() => {
    if (!checkPendingDocsAlert?.isSuccess && checkPendingDocsAlert?.response === null) return;
    if (!checkPendingDocsAlert?.isSuccess && checkPendingDocsAlert?.response !== null) return;

    const isRecheck = dischargeCheckSourceRef.current === 'RECHECK';
    // Already APPROVED, or nothing outstanding — the BE drops an overdue recert/WPN from this list
    // when no submitted note with minutes followed its due date (THIN-7873), which makes an empty
    // list the common outcome for exactly the population that change unblocks. THIN-7875 moved this
    // read into a shared helper: the therapist-dashboard copy of it had drifted onto a stale
    // response shape and silently stopped gating altogether.
    const { canSkipModal } = getRecheckDischargeDecision(checkPendingDocsAlert?.response);

    // Create Document → Discharge and the discharge is already APPROVED, or nothing is
    // outstanding → skip the modal and navigate straight to creating the discharge document.
    // Only on the create/edit path: the explicit "ReCheck for Discharge" button below asked to SEE
    // the alerts, so it keeps its modal and renders the empty state instead.
    if (!isRecheck && canSkipModal) {
      // Clear the consumed pending-docs response so it can't re-trigger this effect.
      dispatch(resetPendingDocsAlertAction());
      const dischargeDoc =
        actionToBeContinue === 'EDIT_DOCUMENT' ? editDocumentAction : navigateDoc;
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document: dischargeDoc,
          activeTab,
          patientData,
          episodeOfCareId,
          ...(actionToBeContinue === 'EDIT_DOCUMENT' ? { requiredDocument: true } : {}),
        },
      });
      return;
    }

    // Otherwise open the alert modal. The modal's isRecheckFlow decides what the action
    // button does: RECHECK → just close; CREATE_DOC → navigate to create the doc.
    setIsRecheckDischargeModal(isRecheck);
    setShowDischargeModal(true);
  }, [checkPendingDocsAlert]);

  // Clear the discharge/approve state when leaving this screen (back or navigating away)
  // so a stale response doesn't re-open the modal / re-trigger navigation on return.
  useEffect(() => {
    return () => {
      dispatch(resetPendingDocsAlertAction());
      dispatch(resetApproveRecheckDischarge());
    };
  }, []);

  const handleMenuItemClick = useCallback(
    async (index: number, template: NavigateDoc): Promise<void> => {
      //  BRK-2074: a menu opened through the target dialog carries the on-behalf context —
      //  thread it onto the document so the persisted hand-off and every interstitial resume
      //  path below carry it unchanged. Single-use: consumed by this click.
      const document: any = onBehalfTarget
        ? {
            ...template,
            onBehalfReason: onBehalfTarget.reason,
            onBehalfOfProviderId: onBehalfTarget.uuid,
            onBehalfOfName: onBehalfTarget.name,
          }
        : template;
      if (onBehalfTarget) {
        setOnBehalfTarget(null);
      }
      dischargeCheckSourceRef.current = 'CREATE_DOC';
      setNavigateDoc(document);
      setSelectedIndex(index);

      setOpen(false);

      //display clock in modal — re-fetch the per-facility flag NOW so this
      //operation uses the latest value. Any error/404 -> false (hide).
      //`handleIsClock` resumes the CREATE_NEW_DOCUMENT flow afterwards.
      const canShowClockInOut = await refetchClockInOutVisibility();
      if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
        setActionToBeContinue('CREATE_NEW_DOCUMENT');
        setIsClockIn(true);
        return;
      }

      dispatch(resetEocDocumentCreateDataAction());
      dispatch(
        getEocDocumentCreateDataAction({
          document,
          activeTab,
          patientData,
          episodeOfCareId,
        })
      );

      //show payer pop-up
      if (
        planOfCareDetails &&
        shouldShowPayerWarning(document, planOfCareDetails?.isPayerPresent, isMultiPayer)
      ) {
        setIsWarningModalOpen(true);
        return;
      }

      //add recert prevent
      if (document.formType?.includes('RECERTIFICATION') && checkIsRecertOverdue(requiredDocs)) {
        setShowRecertModal(true);
        return;
      }

      //add discharge prevent
      if (isRecheckDischarge && document.formType?.includes('DISCHARGE')) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: episodeOfCareId,
            params: {
              discipline: activeTab,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        return;
      }
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document,
          activeTab,
          patientData,
          episodeOfCareId,
        },
      });
    },
    [
      activeTab,
      patientData,
      notToShowAgain,
      isRunning,
      refetchClockInOutVisibility,
      requiredDocs,
      onBehalfTarget,
    ]
  );

  // "ReCheck for Discharge" shortcut. Mirror the Create Document → Discharge path so
  // the discharge modal receives a valid `document`: seed navigateDoc + actionToBeContinue
  // and the EOC create-data before firing the pending-docs check (which opens the modal).
  // Previously this only dispatched checkPendingDocsAlert, leaving the modal's `document`
  // prop empty so Continue navigated with no document.
  const handleRecheckForDischarge = useCallback(() => {
    const dischargeTemplate = allTemplates?.find((t: any) =>
      (t?.formType ?? '').toUpperCase().includes('DISCHARGE')
    );
    if (!dischargeTemplate) return;

    dischargeCheckSourceRef.current = 'RECHECK';
    setNavigateDoc(dischargeTemplate);
    setActionToBeContinue('CREATE_NEW_DOCUMENT');
    dispatch(resetEocDocumentCreateDataAction());
    dispatch(
      getEocDocumentCreateDataAction({
        document: dischargeTemplate,
        activeTab,
        patientData,
        episodeOfCareId,
      })
    );
    dispatch(
      checkPendingDocsAlertAction({
        eocId: episodeOfCareId,
        params: {
          discipline: activeTab,
          localDate: moment(new Date()).format('YYYY-MM-DD'),
        },
      })
    );
  }, [allTemplates, activeTab, patientData, episodeOfCareId]);

  //  BRK-2074: open the target+reason dialog for a FRESH session — close any template menu a
  //  previous confirm opened and drop its target. The dialog's mount resets the shared
  //  target-map reducer, which would otherwise strand that still-open menu filtering against
  //  nothing (and a reason must be re-collected per creation anyway).
  const openOnBehalfCreateDialog = (pending: { intent: 'MENU' | 'ROW'; document?: any }) => {
    setOpen(false);
    setOnBehalfTarget(null);
    setOnBehalfCreate(pending);
  };

  const handleToggle = useCallback((): void => {
    //  BRK-2074: on a discipline-mismatched tab the Create affordance exists only through the
    //  compliance grant — collect the target + reason first; the template menu (filtered by the
    //  TARGET's need-supervision map) opens on the dialog's confirm.
    if (complianceCreateForTab) {
      openOnBehalfCreateDialog({ intent: 'MENU' });
      return;
    }
    setOpen((prevOpen) => !prevOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceCreateForTab]);

  useEffect(() => {
    dispatch(resetUuidAction());
  }, []);

  const handleClose = useCallback((event: Event): void => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  }, []);

  const showDocumentForEdit = useCallback(
    async (_event: any, document: any) => {
      dischargeCheckSourceRef.current = 'CREATE_DOC';
      setEditDocumentAction(document);
      //display clock in modal — re-fetch the per-facility flag NOW so this edit
      // uses the latest value (no page/tab refresh). Any error/404 -> false (hide).
      const canShowClockInOut = await refetchClockInOutVisibility();
      if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
        setActionToBeContinue('EDIT_DOCUMENT');
        setIsClockIn(true);
        return;
      }
      dispatch(resetEocDocumentCreateDataAction());
      dispatch(
        getEocDocumentCreateDataAction({
          document,
          activeTab,
          patientData,
          episodeOfCareId,
          requiredDocument: true,
        })
      );

      if (
        planOfCareDetails &&
        shouldShowPayerWarning(document, planOfCareDetails?.isPayerPresent, isMultiPayer)
      ) {
        setIsWarningModalOpen(true);
        return;
      }

      if (document.formType?.includes('RECERTIFICATION') && checkIsRecertOverdue(requiredDocs)) {
        setShowRecertModal(true);
        return;
      }

      //add discharge prevent
      if (isRecheckDischarge && document.formType?.includes('DISCHARGE')) {
        dispatch(
          checkPendingDocsAlertAction({
            eocId: episodeOfCareId,
            params: {
              discipline: activeTab,
              localDate: moment(new Date()).format('YYYY-MM-DD'),
            },
          })
        );
        return;
      }
      navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${createDocument}`, {
        state: {
          document,
          activeTab,
          patientData,
          episodeOfCareId,
          requiredDocument: true,
        },
      });
    },
    [
      document,
      activeTab,
      patientData,
      episodeOfCareId,
      notToShowAgain,
      isRunning,
      refetchClockInOutVisibility,
      requiredDocs,
    ]
  );

  //  BRK-2074: the dialog's confirm. Menu path: stash the target + reason and open the template
  //  menu. Row path: thread the on-behalf context onto the required-doc row and replay the
  //  EXISTING create funnel (clock-in / payer / recert / discharge interstitials untouched) —
  //  the fields ride the document into the persisted hand-off and onto the webhook mint.
  const confirmOnBehalfCreate = async (target: any, reason: string) => {
    const pending = onBehalfCreate;
    setOnBehalfCreate(null);
    if (pending?.intent === 'ROW') {
      await showDocumentForEdit(null, {
        ...pending.document,
        onBehalfReason: reason,
        onBehalfOfProviderId: target.uuid,
        onBehalfOfName: target.name,
      });
      return;
    }
    setOnBehalfTarget({ uuid: target.uuid, name: target.name, reason });
    setOpen(true);
  };

  const checkEditPermission = (document: any) => {
    // Prefer signatureContext (THIN-7160) — CAN_EDIT on the therapist axis when current
    // user is the form author with edit permission, or on the supervisor axis when
    // current user is the assigned cosigner (THIN-6967). Falls back to legacy
    // form-type-permission gates when ctx is missing.
    const ctx = document?.signatureContext;
    if (ctx) {
      return (
        ctx.allowedTherapistSignatureActions?.includes(SignatureAction.CAN_EDIT) ||
        ctx.allowedSupervisorSignatureActions?.includes(SignatureAction.CAN_EDIT)
      );
    }
    if (document.formType?.includes('DAILY_SOAP_NOTE')) {
      return false;
    }

    // Recert forms need Update-Recertification, eval forms need Update-Eval.
    if (!canUpdateFormType(hasScope, document.formType)) {
      return false;
    }
    return true;
  };

  const HandleStartClock = () => {
    const millageInfo = {
      locationType: 'BUILDING',
      payByMilage: null,
    };
    startStopwatch('MILLAGE', millageInfo);
  };

  const [openDeleteAlertModal, setOpenDeleteModal] = useState<boolean>(false);
  const [forDeleteDocument, setForDeletedocument] = useState<DocumentData | null>(null);

  const handleDeleteRequiredDocument = () => {
    dispatch(deleteRequiredDocumentAction(forDeleteDocument?.uuid || ''));
  };

  const openDeleteConfirmModal = (document: DocumentData) => {
    setForDeletedocument(document);
    setOpenDeleteModal(true);
  };

  const closeDeleteConfirmModal = () => {
    setForDeletedocument(null);
    dispatch(resetDeleteRequiredDocumentAction());
    setOpenDeleteModal(false);
  };

  useEffect(() => {
    if (checkDeleteRequiredDocument.response === null && !checkDeleteRequiredDocument.isDocDeleted)
      return;
    if (checkDeleteRequiredDocument.isDocDeleted) {
      callRequiredDocuments();
      closeDeleteConfirmModal();
    }
  }, [checkDeleteRequiredDocument]);
  const theme = useTheme();

  const createSortHandler = (columnId: string, index: number) => {
    const isAsc = sortOrderArray[index] === 'asc';
    const newSortOrderArray = ['desc', 'desc', 'desc', 'desc', 'desc'];
    newSortOrderArray[index] = isAsc ? 'desc' : 'asc';

    dispatch(
      getEpisodeOfCareRequiredDocument({
        therapistId: therapistId,
        signal: abortControllerScheduledDocumentRef.current.signal,
        params: {
          episodeOfCareId: episodeOfCareId,
          type: activeTab,
          page,
          pageSize: rowPerPage,
          sortBy: columnId,
          sortDirection: getSortOrder1(columnId, newSortOrderArray),
        },
      })
    );
    setSortArray(newSortOrderArray);
    setSortBy(columnId);
  };

  const getSortOrder1 = (sortColumn: string, newSortOrderArray: any) => {
    switch (sortColumn) {
      case 'documentType':
        return newSortOrderArray[0];
      case 'dueDate':
        return newSortOrderArray[1];
      case 'dueStatus':
        return newSortOrderArray[2];
      default:
        return '';
    }
  };

  const getSortOrder = (sortColumn: string) => {
    switch (sortColumn) {
      case 'documentType':
        return sortOrderArray[0];
      case 'dueDate':
        return sortOrderArray[1];
      case 'dueStatus':
        return sortOrderArray[2];
      default:
        return '';
    }
  };

  const oldestUuid = useMemo(() => {
    return episodeOfCareRequiredDocumentData.response?.content
      ? getOldestRequiredDocumentUuid([...episodeOfCareRequiredDocumentData.response.content])
      : undefined;
  }, [episodeOfCareRequiredDocumentData.response?.content]);

  return (
    <Box
      // sx={{ border: '1px solid #F2F4F7', borderRadius: '20px' }}
      p={3}
    >
      {approvePopUp.message && (
        <SimpleSnackbar
          message={approvePopUp.message}
          isError={approvePopUp.type === 'ERROR'}
        />
      )}
      <Card>
        <Grid
          container
          sx={{ padding: '18px 24px 18px 24px', position: 'relative' }}
        >
          <Typography variant="h6">Required Documents</Typography>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.26)',
              borderRadius: '12px',
              padding: '3px',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '12px',
            }}
          >
            <Typography sx={{ color: '#111927', fontSize: '13px', letterSpacing: '0.16px' }}>
              {episodeOfCareRequiredDocumentData?.response?.totalElements
                ? `${episodeOfCareRequiredDocumentData?.response?.totalElements} Documents`
                : `0 Document`}
            </Typography>
          </Box>
          {isRecheckDischarge && recheckOverrideStatus === true && hasDischargeTemplate && (
            <span
              style={{
                position: 'absolute',
                right: showCreateDocument ? 180 : 0,
                display: 'inline-block',
              }}
            >
              <Button
                variant="text"
                startIcon={<CheckBoxOutlined />}
                onClick={handleRecheckForDischarge}
              >
                ReCheck for Discharge
              </Button>
            </span>
          )}
          {showCreateDocument && (
            <Tooltip
              title={
                getAllTemplates.loading
                  ? 'Loading templates...'
                  : isPatientDataLoaded
                  ? 'Loading patient data...'
                  : episodeOfCareData?.status !== 'OPENED'
                  ? 'Episode of care must be opened to create documents.'
                  : ''
              }
              arrow
              placement="left"
            >
              <span style={{ position: 'absolute', right: 0, display: 'inline-block' }}>
                <Button
                  onClick={handleToggle}
                  data-testid={eocTestId('create-document')}
                  startIcon={
                    getAllTemplates.loading || isPatientDataLoaded ? (
                      <img
                        src={loader}
                        style={{ height: 30 }}
                        alt=""
                      />
                    ) : (
                      <Add />
                    )
                  }
                  ref={anchorRef}
                  disabled={
                    getAllTemplates.loading ||
                    isPatientDataLoaded ||
                    episodeOfCareData?.status !== 'OPENED'
                  }
                >
                  Create Document
                </Button>
              </span>
            </Tooltip>
          )}

          <Popper
            anchorEl={anchorRef.current}
            open={open}
            transition
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px',
                  background: '#fff',
                  width: 'auto',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList>
                      {allTemplates?.map((template: any, index: number) => {
                        //  BRK-2074: a menu opened through the target dialog filters by the
                        //  TARGET's map (what the target may author), not the caller's; addendum
                        //  notes are out of the on-behalf scope (their legacy JotForm URL
                        //  prefills the CALLER's identity).
                        return (
                          (onBehalfTarget
                            ? !template.formType?.includes('ADDENDUM_NOTE') &&
                              checkSupervisorPermission(
                                targetSignatureAccess.response?.data?.t2,
                                template.formType
                              )
                            : checkSupervisorPermission(
                                getSignatureAccess.response?.data?.t2,
                                template.formType
                              )) && (
                            <MenuItem
                              key={template.uuid}
                              onClick={() => handleMenuItemClick(index, template)}
                              data-testid={eocRowTestId(
                                'create-document-template',
                                template?.formType
                              )}
                              selected={index === selectedIndex}
                              sx={{ background: 'yello' }}
                            >
                              <Grid
                                container
                                sx={{ padding: '4px 8px 4px 8px' }}
                              >
                                <SvgIcon>{<GetDocumentIcon docType={template.formType} />}</SvgIcon>
                                <Typography sx={{ fontSize: '12px', marginLeft: '6px' }}>
                                  {template.formType
                                    ?.split('_')
                                    .map((item: string) =>
                                      ['PT', 'OT', 'SLP'].includes(item)
                                        ? item
                                        : item.slice(0, 1) +
                                          item.slice(1, item.length).toLowerCase()
                                    )
                                    .join(' ')
                                    .replace('Reform ', ' ')
                                    // ADDENDUM_NOTE_REFORM ends in "Reform" (no trailing space),
                                    // so the replace above misses it. Strip a trailing " Reform"
                                    // to show "Addendum Note". Anchored to end so mid-string
                                    // reform types (e.g. "Evaluation Reform PT") are untouched.
                                    .replace(/ Reform$/, '')}
                                  {draftDocuments.includes(template.formType) && (
                                    <Chip
                                      variant="outlined"
                                      color="warning"
                                      size="small"
                                      label="DRAFT"
                                      sx={{
                                        marginLeft: '0.5rem',
                                        borderColor: theme.palette.warning.main,
                                        fontSize: '0.625rem',
                                      }}
                                    />
                                  )}
                                </Typography>
                              </Grid>
                            </MenuItem>
                          )
                        );
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Grid>
        <Divider />
        <Scrollbar>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {columns?.map((column: any, index: any) => (
                  <TableCell
                    key={column.id}
                    align={column?.align}
                  >
                    {column.id !== 'action' && column.id !== 'visit' ? (
                      <TableSortLabel
                        active={true}
                        direction={sortOrderArray[index]}
                        onClick={() => createSortHandler(column.id, index)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {episodeOfCareRequiredDocumentData?.loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : episodeOfCareRequiredDocumentData?.response?.totalElements ? (
                episodeOfCareRequiredDocumentData.response?.content.map(
                  (e: DocumentData, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{e.documentType}</TableCell>
                        <TableCell>
                          {Number(e?.visits?.split('/')[0]) === 0 && e.dueDate === null ? (
                            'Supervisory visit has not been scheduled'
                          ) : (
                            <Box>
                              {e.dueDate
                                ? format(
                                    new Date(
                                      new Date(e.dueDate).valueOf() +
                                        new Date(e.dueDate).getTimezoneOffset() * 60 * 1000
                                    ),
                                    'dd MMM, yyyy'
                                  )
                                : '-'}
                            </Box>
                          )}
                          {e.dueDate !== null && (
                            <Typography
                              sx={{ color: '#6C737F' }}
                              variant="caption"
                            >
                              {getDisplayString(
                                e.dueDate,
                                true,
                                e,
                                e.maxProgressNoteDays ?? docSettings?.progressNoteDays,
                                '',
                                isDocDuePNAlert
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Status label={e?.visits || '-'} />
                        </TableCell>
                        <TableCell>
                          <Status label={e?.status || '-'} />
                        </TableCell>
                        <TableCell align="right">
                          {((therapistDiscipline === e.type &&
                            checkSupervisorPermission(
                              getSignatureAccess.response?.data?.t2,
                              e?.formType
                            )) ||
                            //  BRK-2074: on a mismatched row the "+" exists only through the
                            //  compliance grant and always routes through the target dialog
                            //  (which validates the TARGET's map for this formType).
                            complianceCreateForRow(e)) &&
                            checkEditPermission(document) &&
                            oldestUuid === e?.uuid && (
                              <IconButton
                                title="Create Document"
                                data-testid={eocRowTestId('create-document-row', e?.uuid)}
                                onClick={(event) =>
                                  complianceCreateForRow(e)
                                    ? openOnBehalfCreateDialog({ intent: 'ROW', document: e })
                                    : showDocumentForEdit(event, e)
                                }
                              >
                                <PlusIcon />
                              </IconButton>
                            )}
                          {isDeleteAlertOn && (
                            <IconButton
                              title="Delete Alert"
                              onClick={() => openDeleteConfirmModal(e)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle2"
                      >
                        No Records Found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Scrollbar>
        <TablePagination
          component="div"
          count={episodeOfCareRequiredDocumentData?.response?.totalElements || 0}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowPerPage}
          rowsPerPageOptions={[10, 15, 30]}
        />
      </Card>

      {/* clock in modal */}
      {canShowClockInDialog() && (
        <BootstrapDialog
          PaperProps={{ style: { maxWidth: '750px' } }}
          onClose={() => setIsClockIn(false)}
          aria-labelledby="customized-dialog-title"
          open={isClockIn && !notToShowAgain}
        >
          <ClockInOutModal
            close={handleIsClock}
            handleClockIn={HandleStartClock}
          />
        </BootstrapDialog>
      )}

      {/* success modal */}

      <Modal open={successModal?.success || successModal?.warning}>
        <SucessModal
          close={handleCloseSuccess}
          isSucess={successModal.success}
          title={successModal?.success ? 'Clock In successfully' : 'Clock in Failed'}
          message=""
        />
      </Modal>

      {/* Delete Alert Modal */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={closeDeleteConfirmModal}
        aria-labelledby="customized-dialog-title"
        open={openDeleteAlertModal}
      >
        <CommonConfirmDeleteModal
          close={closeDeleteConfirmModal}
          handleDelete={handleDeleteRequiredDocument}
          title="Delete Document Alert"
          message={`Are you sure you want to delete this request? - ${forDeleteDocument?.documentType}`}
          isDisabled={checkDeleteRequiredDocument?.loading}
        />
      </BootstrapDialog>

      {/* Discharge Alert */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '100%' } }}
        onClose={() => setShowDischargeModal(false)}
        aria-labelledby="customized-dialog-title"
        open={showDischargeModal}
      >
        <DischargeRecheckModal
          handleDelete={() => setShowDischargeModal(false)}
          title="and Discharge"
          message={`The following items have not been completed.`}
          buttontext="Got it"
          requiredDocument={actionToBeContinue === 'EDIT_DOCUMENT' ? true : false}
          document={actionToBeContinue === 'CREATE_NEW_DOCUMENT' ? navigateDoc : editDocumentAction}
          source="EOC"
          episodeOfCareId={episodeOfCareId}
          discipline={activeTab}
          isRecheckFlow={isRecheckDischargeModal}
        />
      </BootstrapDialog>

      {/* Recert Alert */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '100%' } }}
        onClose={() => setShowRecertModal(false)}
        aria-labelledby="customized-dialog-title"
        open={showRecertModal}
      >
        <InfoConfirmModal
          close={() => setShowRecertModal(false)}
          message={
            'To ensure clinical details prefill accurately, please complete all overdue Progress Notes before the Recertification'
          }
          title="Complete Overdue Progress Notes First"
          handleSave={handleContinueRecert}
          cancelBtnText="Cancel"
          saveBtnText="Start Recertification"
        />
      </BootstrapDialog>
      <WarningModal
        open={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        title={'Missing Payer Information'}
        message={
          'A payer must be assigned before you can create a certification. Please contact your DOR to update the payer details for this patient.'
        }
        buttonText={'Got it'}
        cancelBtn={'Cancel'}
        handleSaveBtn={() => setIsWarningModalOpen(false)}
      />

      {/* BRK-2074: create on behalf — target + reason collected before the funnel runs */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setOnBehalfCreate(null)}
        open={Boolean(onBehalfCreate)}
      >
        {onBehalfCreate && (
          <SelectTargetTherapist
            onClose={() => setOnBehalfCreate(null)}
            onConfirm={confirmOnBehalfCreate}
            activeTab={activeTab}
            //  '' (not null) for a row missing its formType — the dialog fails closed on it.
            formType={
              onBehalfCreate.intent === 'ROW' ? onBehalfCreate.document?.formType ?? '' : null
            }
          />
        )}
      </BootstrapDialog>
    </Box>
  );
};

export default ViewDocumentList;
